import { useState } from "react";
import SEO from "../components/SEO";
import EquivalenciesVisualizer from "../components/EquivalenciesVisualizer";
import { exportToPDF } from "../utils/pdfExport";
import {Heart, Users, MapPin, UtensilsCrossed, Download, Share2} from 'lucide-react';

export default function WeddingCalculator() {
  const [guests, setGuests] = useState("");
  const [avgDistance, setAvgDistance] = useState("");
  const [mealType, setMealType] = useState("mixed");
  const [venueType, setVenueType] = useState("local");
  const [decorType, setDecorType] = useState("mixed");
  const [emissions, setEmissions] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{travel: number; food: number; venue: number; decor: number} | null>(null);

  const calculateEmissions = () => {
    const guestCount = parseFloat(guests) || 0;
    const distance = parseFloat(avgDistance) || 0;

    // Travel emissions: 0.404 kg CO2 per mile per guest (car average)
    const travelEmissions = (guestCount * distance * 0.404 * 2) / 1000; // round trip

    // Food emissions per guest
    const mealFactors: Record<string, number> = {
      "vegan": 0.015, // tons CO2e per guest
      "vegetarian": 0.025,
      "mixed": 0.045,
      "meat-heavy": 0.065,
    };
    const foodEmissions = guestCount * mealFactors[mealType];

    // Venue emissions
    const venueFactors: Record<string, number> = {
      "outdoor": 0.5,
      "local": 1.5,
      "hotel": 3.0,
      "destination": 8.0,
    };
    const venueEmissions = venueFactors[venueType];

    // Decor and waste
    const decorFactors: Record<string, number> = {
      "minimal": 0.3,
      "mixed": 1.2,
      "elaborate": 2.5,
    };
    const decorEmissions = decorFactors[decorType];

    const total = travelEmissions + foodEmissions + venueEmissions + decorEmissions;
    setEmissions(parseFloat(total.toFixed(2)));
    setBreakdown({
      travel: parseFloat(travelEmissions.toFixed(2)),
      food: parseFloat(foodEmissions.toFixed(2)),
      venue: parseFloat(venueEmissions.toFixed(2)),
      decor: parseFloat(decorEmissions.toFixed(2)),
    });
  };

  const shareScore = () => {
    const text = `I calculated my wedding's carbon footprint: ${emissions} tons CO₂e. Plan your green wedding at www.greenhousegascalculator.com/wedding`;
    if (navigator.share) {
      navigator.share({ title: "Wedding Carbon Footprint", text });
    } else {
      navigator.clipboard.writeText(text);
      alert("Score copied to clipboard!");
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Wedding Carbon Footprint Calculator",
    "url": "https://www.greenhousegascalculator.com/wedding",
    "description": "Calculate the carbon footprint of your wedding or special event. Plan a green wedding in 2026 with our free calculator.",
    "applicationCategory": "UtilityApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className="bg-white">
      <SEO
        title="Wedding Carbon Footprint Calculator | Plan a Green Wedding 2026"
        description="Free wedding carbon footprint calculator. Calculate emissions from guests, travel, food, and venue. Learn how to plan a sustainable green wedding and reduce the average 63 tons of CO2."
        keywords="wedding carbon footprint calculator, green wedding, sustainable wedding, eco-friendly wedding, wedding emissions calculator"
        canonical="https://www.greenhousegascalculator.com/wedding"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-pink-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Heart className="w-16 h-16 text-[#2E8B57]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Wedding Carbon Footprint Calculator
            </h1>
            <p className="text-xl text-gray-600">
              Plan your dream green wedding and measure its environmental impact
            </p>
          </div>
        </div>
      </section>

      {/* Educational Article */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Plan a Green Wedding in 2026
            </h2>
            
            <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-8">
              <p className="text-lg font-semibold text-gray-900">
                Did you know? The average wedding produces <span className="text-red-600">63 tons of CO₂</span> - equivalent to 4 people's annual emissions!
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              The Environmental Impact of Weddings
            </h3>
            <p className="text-gray-700 mb-4">
              Weddings are joyous celebrations, but they come with a significant environmental cost. From guest travel to food waste, venue energy consumption to single-use decorations, every element contributes to your wedding's carbon footprint.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
              5 Ways to Reduce Your Wedding Emissions
            </h3>
            <div className="space-y-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">1. Choose a Local Venue</h4>
                <p className="text-gray-700">Guest travel accounts for 60% of wedding emissions. A venue close to most guests dramatically reduces carbon impact.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">2. Serve Plant-Based Options</h4>
                <p className="text-gray-700">A vegetarian menu can cut food-related emissions by 50%. Offer creative, delicious plant-based dishes your guests will love.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">3. Use Sustainable Decor</h4>
                <p className="text-gray-700">Choose potted plants over cut flowers, rent decorations instead of buying, and use LED lighting to minimize waste and energy use.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">4. Digital Invitations</h4>
                <p className="text-gray-700">E-invitations save paper, printing emissions, and postal delivery carbon costs. They're also easier to manage!</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">5. Donate Leftover Food</h4>
                <p className="text-gray-700">Partner with local food banks to donate excess catering. This reduces waste while helping your community.</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Calculate Your Wedding's Carbon Footprint
            </h2>

            <div className="space-y-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Number of Guests</label>
                </div>
                <input
                  type="number"
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  placeholder="e.g., 150"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Average Guest Travel Distance</label>
                </div>
                <input
                  type="number"
                  value={avgDistance}
                  onChange={(e) => setAvgDistance(e.target.value)}
                  placeholder="Miles (one-way)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                />
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <UtensilsCrossed className="w-5 h-5 text-[#2E8B57]" />
                  <label className="font-semibold text-gray-900">Meal Type</label>
                </div>
                <select
                  value={mealType}
                  onChange={(e) => setMealType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="vegan">Fully Vegan</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="mixed">Mixed (Meat & Veg Options)</option>
                  <option value="meat-heavy">Meat-Heavy</option>
                </select>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <label className="font-semibold text-gray-900 block mb-4">Venue Type</label>
                <select
                  value={venueType}
                  onChange={(e) => setVenueType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="outdoor">Outdoor/Backyard</option>
                  <option value="local">Local Venue</option>
                  <option value="hotel">Hotel/Resort</option>
                  <option value="destination">Destination Wedding</option>
                </select>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <label className="font-semibold text-gray-900 block mb-4">Decorations & Style</label>
                <select
                  value={decorType}
                  onChange={(e) => setDecorType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8B57]"
                >
                  <option value="minimal">Minimal/Sustainable</option>
                  <option value="mixed">Moderate Decor</option>
                  <option value="elaborate">Elaborate/Luxury</option>
                </select>
              </div>
            </div>

            <button
              onClick={calculateEmissions}
              className="w-full bg-[#2E8B57] text-white py-4 rounded-lg font-semibold hover:bg-[#267347] transition-colors"
            >
              Calculate Wedding Emissions
            </button>

            {emissions !== null && breakdown && (
              <div className="mt-8 space-y-4">
                <div className="bg-gradient-to-r from-pink-50 to-green-50 p-6 rounded-xl border-2 border-[#2E8B57]">
                  <div className="text-center">
                    <p className="text-gray-600 mb-2">Your Wedding's Carbon Footprint</p>
                    <p className="text-4xl font-bold text-[#2E8B57]">
                      {emissions} tons CO₂e
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {emissions < 20 ? "🌟 Excellent! Well below average!" : 
                       emissions < 40 ? "✅ Good! Below the 63-ton average" :
                       emissions < 63 ? "⚠️ Above average - consider reductions" :
                       "🚨 Significantly above average"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-4">Emissions Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Guest Travel</span>
                      <span className="font-semibold">{breakdown.travel} tons ({Math.round((breakdown.travel/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Food & Catering</span>
                      <span className="font-semibold">{breakdown.food} tons ({Math.round((breakdown.food/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Venue & Energy</span>
                      <span className="font-semibold">{breakdown.venue} tons ({Math.round((breakdown.venue/emissions)*100)}%)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Decor & Waste</span>
                      <span className="font-semibold">{breakdown.decor} tons ({Math.round((breakdown.decor/emissions)*100)}%)</span>
                    </div>
                  </div>
                </div>

                <EquivalenciesVisualizer emissions={emissions} />

                <div className="flex gap-4">
                  <button
                    onClick={() => exportToPDF({
                      title: "Wedding Carbon Footprint Report",
                      totalEmissions: emissions,
                      breakdown,
                      calculationType: "wedding"
                    })}
                    className="flex-1 bg-[#2E8B57] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#267347] transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export Report
                  </button>
                  <button
                    onClick={shareScore}
                    className="flex-1 bg-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Share Your Score
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
